/// <amd-module name="Wit/Common/WorkItemDialogs/Components/BulkEditFieldsTable" />

import * as React from "react";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { endsWith, format, localeIgnoreCaseComparer, newGuid, startsWith } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { AddTagsPseudoRefName, RemoveTagsPseudoRefName } from "Wit/Common/BulkUpdateUtils/TagUtils";
import { CoreFieldRefNames, FieldFlags, FieldUsages, OobFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { FieldValue, WorkItemFieldControl } from "Wit/Common/WorkItemControls/WorkItemFieldControl";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";

export interface IBulkEditFieldsTableProps {
    clauses: IObservableArray<Clause>;
    fields: IFieldEntry[];
    getAllowedValues?: (field: IFieldEntry) => Promise<string[] | undefined>;
    onValidate?: (valid: boolean) => void;
    projectId: string;
    scrollable?: boolean;
}

interface IBulkEditFieldsTableState {
    errorMessage?: string;
}

export interface Clause {
    allowedValues: IObservableArray<string>;
    field: IObservableValue<IFieldEntry | undefined>;
    id: string;
    dirty?: boolean;
    value: IObservableValue<FieldValue>;
}

const ExcludedFromBulkEditFields = new Set([
    CoreFieldRefNames.AreaId,
    CoreFieldRefNames.AttachedFileCount,
    CoreFieldRefNames.AuthorizedAs,
    CoreFieldRefNames.AuthorizedDate,
    CoreFieldRefNames.BoardColumn,
    CoreFieldRefNames.BoardColumnDone,
    CoreFieldRefNames.BoardLane,
    CoreFieldRefNames.ChangedBy,
    CoreFieldRefNames.CreatedBy,
    CoreFieldRefNames.ChangedDate,
    CoreFieldRefNames.CreatedDate,
    CoreFieldRefNames.ExternalLinkCount,
    CoreFieldRefNames.History,
    CoreFieldRefNames.HyperLinkCount,
    CoreFieldRefNames.Id,
    CoreFieldRefNames.IterationId,
    CoreFieldRefNames.LinkType,
    CoreFieldRefNames.NodeName,
    CoreFieldRefNames.RelatedLinkCount,
    CoreFieldRefNames.Rev,
    CoreFieldRefNames.RevisedDate,
    CoreFieldRefNames.TeamProject,
    CoreFieldRefNames.Watermark,
    CoreFieldRefNames.WorkItemType,
    CoreFieldRefNames.IsDeleted,
    OobFieldRefNames.BacklogPriority,
    OobFieldRefNames.StackRank,
    OobFieldRefNames.ActivatedBy,
    OobFieldRefNames.ActivatedDate,
    OobFieldRefNames.ClosedBy,
    OobFieldRefNames.ClosedDate,
    OobFieldRefNames.ResolvedBy,
    OobFieldRefNames.ResolvedDate,
    OobFieldRefNames.StateChangeDate,
    "System.PersonId"
]);

export class BulkEditFieldsTable extends VssComponent<IBulkEditFieldsTableProps, IBulkEditFieldsTableState> {
    private fields = new ObservableArray<IListBoxItem<IFieldEntry>>([]);
    private fieldToAllowedValues = new Map<string, Promise<string[] | undefined>>();

    constructor(props: IBulkEditFieldsTableProps, context: IVssComponentContext) {
        super(props, context);
        this.updateAvailableFields();
        this.state = {};
    }

    public componentDidMount() {
        this.validate();
        this.props.clauses.subscribe(this.onClausesChanged);
    }

    public componentDidUpdate(prevProps: IBulkEditFieldsTableProps): void {
        if (this.props.fields !== prevProps.fields) {
            this.updateAvailableFields();
        }
    }

    public componentWillUnmount() {
        this.props.clauses.unsubscribe(this.onClausesChanged);
    }

    public render(): JSX.Element {
        return (
            <div className="flex-grow bulk-edit-table-container rhythm-vertical-16">
                {this.state.errorMessage && (
                    <div className="flex-row flex-center rhythm-horizontal-8">
                        <Status {...Statuses.Failed} key="error" size={StatusSize.m} />
                        <span className="error-text">{this.state.errorMessage}</span>
                    </div>
                )}
                <Table
                    className="bulk-edit-table"
                    columns={this.columns}
                    itemProvider={this.props.clauses}
                    spacerWidth={0}
                    scrollable={this.props.scrollable}
                ></Table>
                <div className="padding-bottom-16">
                    <Button
                        iconProps={{ iconName: "Add" }}
                        onClick={() => {
                            this.props.clauses.push({
                                allowedValues: new ObservableArray([]),
                                id: newGuid(),
                                field: new ObservableValue<IFieldEntry | undefined>(undefined),
                                value: new ObservableValue<string>("")
                            });
                            this.validate();
                        }}
                        text={Resources.AddField}
                    />
                </div>
            </div>
        );
    }

    private onClausesChanged = (): void => {
        this.validate();
    };

    private updateAvailableFields(): void {
        const fields = this.props.fields;
        const filteredFields = fields.filter((field: IFieldEntry) => {
            return !isExcludedFromBulkEdit(field);
        });
        const filteredFieldsWithTags = this.addTagFields(filteredFields);
        filteredFieldsWithTags.sort((a, b) => localeIgnoreCaseComparer(a.name, b.name));
        this.fields.value = filteredFieldsWithTags.map((field: IFieldEntry) => ({ id: field.id.toString(), text: field.name, data: field }));
    }

    private columns: ITableColumn<Clause>[] = [
        {
            id: "add-remove",
            readonly: true,
            // width of buttons + cell padding
            width: 76,
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Clause>, clause: Clause) => {
                return (
                    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={clause.id + "-col-" + columnIndex}>
                        <Button
                            ariaLabel={Resources.InsertNewField}
                            className="add-line-button"
                            iconProps={{ iconName: "Add" }}
                            tooltipProps={{ text: Resources.InsertNewField }}
                            onClick={() => {
                                this.props.clauses.splice(rowIndex, 0, {
                                    allowedValues: new ObservableArray([]),
                                    field: new ObservableValue<IFieldEntry | undefined>(undefined),
                                    id: newGuid(),
                                    value: new ObservableValue<string>("")
                                });
                                this.validate();
                            }}
                        />
                        <Button
                            ariaLabel={Resources.RemoveField}
                            className="remove-line-button"
                            iconProps={{ iconName: "Cancel" }}
                            tooltipProps={{ text: Resources.RemoveField }}
                            onClick={() => {
                                this.props.clauses.splice(rowIndex, 1);
                                this.validate();
                            }}
                        />
                    </SimpleTableCell>
                );
            }
        },
        {
            id: "field",
            name: Resources.FieldColumn,
            width: -40,
            renderCell: (rowIndex: number, columnindex: number, tableColumn: ITableColumn<Clause>, clause: Clause) => {
                return (
                    <SimpleTableCell columnIndex={columnindex} key={"field-" + clause.id}>
                        <FormItem className="field-form-item">
                            <EditableDropdown
                                allowTextSelection
                                ariaLabel={Resources.FieldColumn}
                                className="field-editable-dropdown"
                                items={this.fields}
                                onValueChange={(value?: IListBoxItem<IFieldEntry>) => this.onFieldValueChange(clause, value)}
                                selectedText={clause.field.value?.name ?? ""}
                            />
                        </FormItem>
                    </SimpleTableCell>
                );
            },
            required: true
        },
        {
            id: "operator",
            width: 34,
            renderCell: (rowIndex: number, columnindex: number, tableColumn: ITableColumn<Clause>, clause: Clause) => {
                return (
                    <SimpleTableCell columnIndex={columnindex} key={"operator-" + clause.id} contentClassName="justify-center">
                        =
                    </SimpleTableCell>
                );
            }
        },
        {
            id: "value",
            name: Resources.ValueColumn,
            width: -60,
            renderCell: (rowIndex: number, columnindex: number, tableColumn: ITableColumn<Clause>, clause: Clause) => {
                return (
                    <SimpleTableCell columnIndex={columnindex} key={"value-" + clause.id}>
                        <FormItem className="flex-grow field-form-item">
                            <Observer allowedValues={clause.allowedValues} selectedField={clause.field}>
                                {(observerProps: { allowedValues?: string[]; selectedField: IFieldEntry | undefined }) => {
                                    return (
                                        <WorkItemFieldControl
                                            allowedValues={observerProps.allowedValues}
                                            ariaLabel={Resources.ValueColumn}
                                            disabled={!observerProps.selectedField}
                                            fieldEntry={observerProps.selectedField}
                                            initialValue={clause.value.value}
                                            key={clause.id}
                                            onChange={value => {
                                                if (value !== clause.value.value) {
                                                    clause.value.value = value;
                                                    clause.dirty = true;
                                                    this.validate();
                                                }
                                            }}
                                        />
                                    );
                                }}
                            </Observer>
                        </FormItem>
                    </SimpleTableCell>
                );
            }
        }
    ];

    private async onFieldValueChange(clause: Clause, selectedItem?: IListBoxItem<IFieldEntry>) {
        const selectedField = selectedItem?.data;
        clause.field.value = selectedItem ? selectedItem.data! : undefined;
        clause.value.value = "";
        clause.dirty = true;

        if (selectedField) {
            let valuesPromise = this.fieldToAllowedValues.get(selectedField.referenceName);
            if (!valuesPromise && this.props.getAllowedValues) {
                valuesPromise = this.props.getAllowedValues(selectedField);
            }

            const allowedValues = await valuesPromise;
            if (allowedValues) {
                const values = clause.allowedValues;
                values.splice(0, values.length, ...allowedValues);
            }
        }

        this.validate();
    }

    private validate(): boolean {
        // require at least one field
        let isValid = !!this.props.clauses.length;
        let errorMessage: string | undefined = undefined;
        const fieldRefNames = new Set<string>();
        for (const clause of this.props.clauses.value) {
            if (!clause.field.value) {
                isValid = false;
            } else if (fieldRefNames.has(clause.field.value.referenceName)) {
                isValid = false;
                errorMessage = Resources.DuplicateFieldsErrorMessage;
                break;
            } else {
                fieldRefNames.add(clause.field.value.referenceName);
            }
        }

        if (errorMessage !== this.state.errorMessage) {
            this.setState({ errorMessage });
        }

        if (this.props.onValidate) {
            this.props.onValidate(isValid);
        }

        return isValid;
    }

    private addTagFields(fieldDefinitions: IFieldEntry[]): IFieldEntry[] {
        const bulkEditableFields: IFieldEntry[] = [];
        for (const fieldDefinition of fieldDefinitions) {
            if (fieldDefinition.referenceName === CoreFieldRefNames.Tags) {
                bulkEditableFields.push({
                    ...fieldDefinition,
                    name: format(Resources.AddTagsFormat, fieldDefinition.name),
                    referenceName: AddTagsPseudoRefName
                });

                bulkEditableFields.push({
                    ...fieldDefinition,
                    name: format(Resources.RemoveTagsFormat, fieldDefinition.name),
                    referenceName: RemoveTagsPseudoRefName
                });
            } else {
                bulkEditableFields.push(fieldDefinition);
            }
        }

        return bulkEditableFields;
    }
}

/**
 * Decide if the field need to be excluded from bulk edit
 * @param fieldDefinition
 */
export function isExcludedFromBulkEdit(fieldDefinition: IFieldEntry): boolean {
    const fieldReferenceName = fieldDefinition.referenceName;
    const workItemUsage = fieldDefinition.usages & FieldUsages.WorkItem;

    if (!isEditable(fieldDefinition) || !workItemUsage || checkFlag(fieldDefinition, FieldFlags.Ignored)) {
        return true;
    }

    if (ExcludedFromBulkEditFields.has(fieldReferenceName)) {
        return true;
    }

    const allowedValues = [
        "Microsoft.VSTS.Build.IntegrationBuild",
        "Microsoft.VSTS.Build.FoundIn",
        "Microsoft.VSTS.TCM.ReproSteps",
        "Microsoft.VSTS.TCM.SystemInfo",
        "Microsoft.VSTS.Common",
        "Microsoft.VSTS.CMMI"
    ];

    if (allowedValues.some(prefix => startsWith(fieldReferenceName, prefix, true))) {
        return false;
    }

    const excludedStartsWith = [
        "System.AreaLevel",
        "System.IterationLevel",
        "Microsoft.VSTS.TCM",
        "Microsoft.VSTS.Build",
        "Microsoft.VSTS.CodeReview",
        "Microsoft.VSTS.Feedback"
    ];
    const excludedEndsWith = ["ExtensionMarker"];

    if (excludedStartsWith.some(prefix => startsWith(fieldReferenceName, prefix, true))) {
        return true;
    }

    if (excludedEndsWith.some(suffix => endsWith(fieldReferenceName, suffix, true))) {
        return true;
    }

    return false;
}

function isComputed(fieldDefinition: IFieldEntry): boolean {
    return checkFlag(fieldDefinition, FieldFlags.Computed);
}

function checkFlag(fieldDefinition: IFieldEntry, flag: FieldFlags): boolean {
    return (fieldDefinition.flags & +flag) !== FieldFlags.None;
}

function isEditable(fieldDefinition: IFieldEntry) {
    if (fieldDefinition.referenceName === CoreFieldRefNames.AreaPath || fieldDefinition.referenceName === CoreFieldRefNames.IterationPath) {
        return true;
    }
    return !isComputed(fieldDefinition);
}
